import React from 'react';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  IntlShape,
} from 'react-intl';
import { Popconfirm, Space, Tooltip, Typography } from 'antd';

import {
  displayInTimezone,
  FORMAT_WITHOUT_SECONDS,
  TIME_24H_FORMAT,
} from 'src/utils/timeUtils';
import AlertIndicator from 'src/components/AlertIndicator';
import Connector, { Props } from './Connector';
import {
  BaselineDetails,
  CardActions,
  CardContent,
  CardDetails,
  CardItems,
  CardWrapper,
  DeleteIconWrapper,
  ItemLabel,
  PatientName,
  RoomDetails,
  StyledDeleteIcon,
  StyledDisconnectionEventType,
  StyledIcon,
  StyledLabel,
  StyledRow,
  StyledTenantName,
  StyledTimestamp,
} from './styled';
import { isTechnicalAlert } from 'src/redux/data/alerts/modules/typeGuards';
import AccessControl from 'src/components/AccessControl';
import permissions from 'src/permissions';
import { ToolTipMessages } from 'src/translations/messages/toolTipMessages';
import { deepEqual } from 'src/utils/comparators';
import { getValueOrNA } from 'src/utils/dataUtils';
import { ALERT_STATUS_ENUM } from 'src/utils/constants';
import { ALERT_METRIC_ENUM } from 'src/redux/data/constants';
import {
  BaseCard,
  BaseCardHeader,
  COMMON_COLORS,
  Icon,
  MetricDisplay,
  MetricDisplayCard,
} from '@neteera/neteera-web-nextgen';
import {
  getMetricDisplayProps,
  getMetricHeaderProps,
} from 'src/components/Sidebars/components/AlertCard/utils';

const { Paragraph } = Typography;

type AllProps = Props & {
  intl: IntlShape;
  config?: {
    displayPatientName?: boolean;
    displaySiteLocation?: boolean;
    displaySimilarNotifications?: boolean;
    allowAlertDelete?: boolean;
  };
  similarNotifications?: {
    count?: number;
    intervalSize?: number;
    intervalUnit?: string;
  };
  background?: string;
};

const AlertCard = ({
  intl,
  alertId,
  alert,
  deviceRoomAndBed,
  patient,
  tenant,
  timezone,
  onPatientNameClick,
  onSuppressAlertClick,
  onUnSuppressAlertClick,
  onDeleteAlertClick,
  config = {
    displayPatientName: true,
    displaySiteLocation: true,
    displaySimilarNotifications: true,
    allowAlertDelete: true,
  },
  similarNotifications,
  background = '#FFF',
  baselineThresholds,
}: AllProps): JSX.Element | null => {
  if (!alert) {
    return null;
  }
  const { startTime, thresholdMetric, status } = alert;
  const isAlertSuppressed = status === ALERT_STATUS_ENUM.SUPPRESSED;
  const confirm = () => {
    onDeleteAlertClick(alertId);
  };

  if (isTechnicalAlert(alert?.thresholdMetric)) {
    return (
      <CardWrapper>
        <CardItems>
          {tenant && <StyledTenantName>{tenant.name}</StyledTenantName>}
          <CardContent>
            <CardDetails>
              <StyledDisconnectionEventType>
                <FormattedMessage {...messages[thresholdMetric]} />
              </StyledDisconnectionEventType>
            </CardDetails>
          </CardContent>
          <Space size={0}>
            <StyledTimestamp>
              {displayInTimezone(startTime, timezone, TIME_24H_FORMAT)}
            </StyledTimestamp>
            <AlertIndicator {...alert} />
          </Space>
        </CardItems>

        <CardActions isTechnicalAlert={true}>
          {/*TODO: need to check if we still need these actions*/}
          {isAlertSuppressed && (
            <AccessControl permissions={[permissions.ALERT_UN_SUPPRESS]}>
              <StyledIcon
                type={'un-suppress-alert-icon'}
                onClick={() => onUnSuppressAlertClick?.(alertId)}
              />
            </AccessControl>
          )}
          {!isAlertSuppressed && (
            <AccessControl permissions={[permissions.ALERT_SUPPRESS]}>
              <StyledIcon
                type={'suppress-alert-icon'}
                onClick={() => onSuppressAlertClick?.(alertId)}
              />
            </AccessControl>
          )}
          {!!config.allowAlertDelete && (
            <AccessControl permissions={[permissions.ALERT_CLEAR_ALL]}>
              <Popconfirm
                title={intl.formatMessage(messages.confirmDelete)}
                onConfirm={confirm}
                okText="Yes"
                cancelText="No"
                placement="right"
              >
                <DeleteIconWrapper>
                  <StyledDeleteIcon type="clear-alert-icon" />
                </DeleteIconWrapper>
              </Popconfirm>
            </AccessControl>
          )}
        </CardActions>
      </CardWrapper>
    );
  }

  if (!deviceRoomAndBed) {
    return null;
  }

  const { roomName, bedName } = deviceRoomAndBed;
  const { firstName = '', lastName = '', isConsented } = patient || {};
  const displayRoomName = getValueOrNA(roomName);
  const displayBedName = getValueOrNA(bedName);
  const isPositionChangeAlert =
    thresholdMetric === ALERT_METRIC_ENUM.POSITION_CHANGE;

  return (
    <Tooltip
      title={<FormattedMessage {...ToolTipMessages.patientNameAlertCard} />}
    >
      <div>
        <BaseCard background={background} className="flex flex-col">
          <BaseCardHeader
            title={displayInTimezone(
              startTime,
              timezone,
              FORMAT_WITHOUT_SECONDS,
            )}
            titleClassName="text-sm font-small"
          >
            {!isPositionChangeAlert && (
              <AccessControl permissions={[permissions.ALERT_CLEAR_ALL]}>
                <Popconfirm
                  title={intl.formatMessage(messages.confirmDelete)}
                  onConfirm={confirm}
                  okText={intl.formatMessage(messages.confirmDeleteYes)}
                  cancelText={intl.formatMessage(messages.confirmDeleteNo)}
                  placement="right"
                >
                  {!!config.allowAlertDelete && (
                    <DeleteIconWrapper>
                      <Icon className={'cursor-pointer'} iconName="Delete" />
                    </DeleteIconWrapper>
                  )}
                </Popconfirm>
              </AccessControl>
            )}
          </BaseCardHeader>
          <MetricDisplayCard
            background={COMMON_COLORS.redLight}
            // @ts-ignore need to import types from UI library
            metricHeader={getMetricHeaderProps(intl, thresholdMetric, alert)}
            // @ts-ignore need to import types from UI library
            metricDisplay={getMetricDisplayProps(
              intl,
              thresholdMetric,
              alert,
              baselineThresholds,
            )}
          />
          {/*TODO: do we want to change the patient Name style?*/}
          {!!config?.displayPatientName && (
            <PatientName
              onClick={event => {
                if (!isConsented) {
                  event.preventDefault();
                  return;
                }
                onPatientNameClick();
              }}
            >
              <Paragraph
                ellipsis={{
                  rows: 1,
                  tooltip: `${firstName} ${lastName}`,
                }}
              >{`${firstName} ${lastName}`}</Paragraph>
            </PatientName>
          )}
          {!!config?.displaySiteLocation && (
            <RoomDetails>
              <Paragraph
                ellipsis={{
                  rows: 1,
                  tooltip: displayRoomName,
                }}
              >
                {displayRoomName}
              </Paragraph>

              <Paragraph
                ellipsis={{
                  rows: 1,
                  tooltip: displayBedName,
                }}
              >
                <ItemLabel>
                  <FormattedMessage {...messages.bed} />
                </ItemLabel>
                {displayBedName}
              </Paragraph>
            </RoomDetails>
          )}
          <BaselineDetails>
            <StyledLabel className="text-sm">
              {intl.formatMessage(messages.baselineLabel)}
            </StyledLabel>
            <StyledRow>
              <MetricDisplay
                iconProps={{
                  iconName: 'Heart',
                }}
                // isLoading={isLoading}
                value={
                  alert?.hrBaselineValue
                    ? Math.round(alert?.hrBaselineValue)
                    : '--'
                }
                valueClasses="text-lg leading-none"
                label={intl.formatMessage(messages.bpmLabel)}
                size="sm"
              />
              <MetricDisplay
                iconProps={{
                  iconName: 'Lungs',
                }}
                value={
                  alert?.rrBaselineValue
                    ? Math.round(alert?.rrBaselineValue)
                    : '--'
                }
                label={intl.formatMessage(messages.brpmLabel)}
                valueClasses="text-lg leading-none"
                size="sm"
              />
            </StyledRow>
          </BaselineDetails>
          {config?.displaySimilarNotifications ? (
            <StyledLabel className="text-sm">
              <span
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage(messages.similarNotifications, {
                    strongStart: '<strong>',
                    strongEnd: '</strong>',
                    value: similarNotifications?.count ?? '0',
                    intervalSize: similarNotifications?.intervalSize ?? 72,
                    intervalUnit:
                      similarNotifications?.intervalUnit ??
                      intl.formatMessage(messages.hourLabel),
                  }),
                }}
              />
            </StyledLabel>
          ) : (
            <></>
          )}
        </BaseCard>
      </div>
    </Tooltip>
  );
};

export const messages = defineMessages({
  HR: {
    id: 'AlertCard.index.HR',
    defaultMessage: 'HR',
  },
  RR: {
    id: 'AlertCard.index.RR',
    defaultMessage: 'RR',
  },
  HR_BASELINE: {
    id: 'AlertCard.index.HR_BASELINE',
    defaultMessage: 'HR',
  },
  RR_BASELINE: {
    id: 'AlertCard.index.RR_BASELINE',
    defaultMessage: 'RR',
  },
  LONG_OUT_OF_BED: {
    id: 'AlertCard.index.LONG_OUT_OF_BED',
    defaultMessage: 'BedExit Duration',
  },
  POSITION_CHANGE: {
    id: 'AlertCard.index.POSITION_CHANGE',
    defaultMessage: 'Position change required',
  },
  BED_EXIT: {
    id: 'AlertCard.index.BED_EXIT',
    defaultMessage: 'BedExit',
  },
  BED_TIME_BASELINE: {
    id: 'AlertCard.index.BED_TIME_BASELINE',
    defaultMessage: 'Bed time',
  },
  BED_EXIT_FREQUENCY: {
    id: 'AlertCard.index.BED_EXIT_FREQUENCY',
    defaultMessage: 'BedExit Frequency',
  },
  DEVICE_DISCONNECTED: {
    id: 'AlertCard.index.DEVICE_DISCONNECTED',
    defaultMessage: 'Disconnected device',
  },
  ALL_DEVICES_DISCONNECTED: {
    id: 'AlertCard.index.ALL_DEVICES_DISCONNECTED',
    defaultMessage: 'All devices are disconnected',
  },
  bed: {
    id: 'AlertCard.index.bed',
    defaultMessage: 'Bed',
  },
  confirmDelete: {
    id: 'AlertCard.index.confirmDelete',
    defaultMessage: 'Are you sure you want to delete this alert?',
  },
  confirmDeleteYes: {
    id: 'AlertCard.index.confirmDelete.yes',
    defaultMessage: 'Yes',
  },
  confirmDeleteNo: {
    id: 'AlertCard.index.confirmDelete.no',
    defaultMessage: 'No',
  },
  baselineLabel: {
    id: 'AlertCard.index.baselineLabel',
    defaultMessage: 'Baseline:',
  },
  bpmLabel: {
    id: 'AlertCard.index.bpmLabel',
    defaultMessage: 'BPM',
  },
  brpmLabel: {
    id: 'AlertCard.index.brpmLabel',
    defaultMessage: 'BRPM',
  },
  hourLabel: {
    id: 'AlertCard.index.hourLabel',
    defaultMessage: 'hour',
  },
  similarNotifications: {
    id: 'AlertCard.index.similarNotifications',
    defaultMessage:
      '{strongStart}{value}{strongEnd} similar {value, select, 1 {notification} other {notifications}} in the past {intervalSize} {intervalUnit}{intervalSize, select, 1 {} other {s}}',
  },
});

export default Connector(
  injectIntl(
    React.memo(AlertCard, (oldProps, newProps) =>
      deepEqual(oldProps, newProps),
    ),
  ),
);
